import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, Eye, Tag, ChevronLeft, Share2, Clock, User, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
    fetchRecentPosts();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/blog/post/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Blog yazısı yüklenemedi:', error);
      setError('Blog yazısı bulunamadı');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog/recent?limit=4`);
      setRecentPosts(response.data.posts || []);
    } catch (error) {
      console.error('Son yazılar yüklenemedi:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateReadTime = (content) => {
    if (!content) return '1 dk';
    const words = content.replace(/<[^>]+>/g, '').split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} dk okuma`;
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: url
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(url);
      alert('Link kopyalandı!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
        <Navbar />
        <div className="flex justify-center items-center py-32">
          <Loader2 className="w-10 h-10 animate-spin text-teal-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-32 text-center">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-5xl">😔</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Blog Yazısı Bulunamadı</h1>
          <p className="text-gray-600 mb-8">Aradığınız içerik mevcut değil veya kaldırılmış olabilir.</p>
          <Button onClick={() => navigate('/blog')} className="bg-teal-600 hover:bg-teal-700">
            <ChevronLeft className="w-5 h-5 mr-2" /> Blog&apos;a Dön
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryColors = {
    'Kombi': 'bg-orange-100 text-orange-700',
    'Klima': 'bg-blue-100 text-blue-700',
    'Beyaz Eşya': 'bg-purple-100 text-purple-700',
    'Elektrik': 'bg-yellow-100 text-yellow-700',
    'Su Tesisatı': 'bg-cyan-100 text-cyan-700',
    'Genel': 'bg-gray-100 text-gray-700'
  };

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.meta_description || post.excerpt,
    "image": post.featured_image || "https://hizmetgeliyo.com/og-image.png",
    "author": {
      "@type": "Organization",
      "name": "HizmetGeliyo",
      "url": "https://hizmetgeliyo.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "HizmetGeliyo",
      "logo": {
        "@type": "ImageObject",
        "url": "https://hizmetgeliyo.com/logo.webp",
        "width": 200,
        "height": 60
      }
    },
    "datePublished": post.published_at,
    "dateModified": post.updated_at || post.published_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://hizmetgeliyo.com/blog/${post.slug}`
    },
    "articleSection": post.category || "Genel",
    "keywords": post.tags?.join(', ') || post.category
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Ana Sayfa",
        "item": "https://hizmetgeliyo.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://hizmetgeliyo.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": `https://hizmetgeliyo.com/blog/${post.slug}`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
      <Helmet>
        <title>{post.meta_title || post.title} - HizmetGeliyo Blog</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <link rel="canonical" href={`https://hizmetgeliyo.com/blog/${post.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.featured_image || "https://hizmetgeliyo.com/og-image.png"} />
        <meta property="og:url" content={`https://hizmetgeliyo.com/blog/${post.slug}`} />
        <meta property="article:published_time" content={post.published_at} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.featured_image || "https://hizmetgeliyo.com/og-image.png"} />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-teal-600">Ana Sayfa</Link>
            <ChevronLeft className="w-4 h-4 rotate-180" />
            <Link to="/blog" className="hover:text-teal-600">Blog</Link>
            <ChevronLeft className="w-4 h-4 rotate-180" />
            <span className="text-gray-800 truncate max-w-[200px]">{post.title}</span>
          </nav>
        </div>
      </div>

      {/* Article Header */}
      <article className="pt-8 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category & Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${categoryColors[post.category] || categoryColors['Genel']}`}>
              {post.category}
            </span>
            <span className="flex items-center gap-1 text-gray-500 text-sm">
              <Calendar className="w-4 h-4" />
              {formatDate(post.published_at)}
            </span>
            <span className="flex items-center gap-1 text-gray-500 text-sm">
              <Clock className="w-4 h-4" />
              {calculateReadTime(post.content)}
            </span>
            <span className="flex items-center gap-1 text-gray-500 text-sm">
              <Eye className="w-4 h-4" />
              {post.views} görüntülenme
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Excerpt */}
          {post.excerpt && (
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {post.excerpt}
            </p>
          )}

          {/* Featured Image */}
          {post.featured_image && (
            <div className="aspect-video rounded-2xl overflow-hidden mb-8 bg-gray-100">
              <img 
                src={post.featured_image} 
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Share Button */}
          <div className="flex items-center gap-4 mb-8 pb-8 border-b">
            <Button 
              variant="outline" 
              onClick={handleShare}
              className="flex items-center gap-2"
            >
              <Share2 className="w-4 h-4" />
              Paylaş
            </Button>
          </div>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-teal-600 prose-strong:text-gray-900 prose-ul:text-gray-700 prose-ol:text-gray-700"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t">
              <h3 className="text-sm font-semibold text-gray-500 mb-4">Etiketler</h3>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, idx) => (
                  <span key={idx} className="flex items-center gap-1 text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-full hover:bg-gray-200 transition-colors">
                    <Tag className="w-4 h-4" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Author Box */}
          <div className="mt-12 p-6 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">HizmetGeliyo Editör</h4>
                <p className="text-gray-600 text-sm">Ev hizmetleri ve bakım ipuçları hakkında faydalı bilgiler paylaşıyoruz.</p>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {recentPosts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Diğer Yazılar</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentPosts.filter(p => p.slug !== post.slug).slice(0, 4).map((relatedPost) => (
                <Link 
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-4 group"
                >
                  <div className="aspect-video bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg mb-4 overflow-hidden">
                    {relatedPost.featured_image ? (
                      <img 
                        src={relatedPost.featured_image} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-4xl">📝</span>
                      </div>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-800 line-clamp-2 group-hover:text-teal-600 transition-colors">
                    {relatedPost.title}
                  </h3>
                  <p className="text-sm text-gray-500 mt-2">
                    {formatDate(relatedPost.published_at)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Profesyonel Servis mi Arıyorsunuz?
          </h2>
          <p className="text-teal-100 mb-8">
            500+ onaylı usta ile kombi, klima, beyaz eşya servisleriniz güvende!
          </p>
          <Button 
            onClick={() => navigate('/')}
            className="bg-white text-teal-600 hover:bg-teal-50 px-8 py-3 rounded-full font-semibold"
          >
            Hemen Teklif Al <ArrowRight className="w-5 h-5 ml-2 inline" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPost;
